import axios from 'axios';
import { extractFromSlug } from 'helpers/utils';
import {
  UPCOMING_LIVE_CLASS_API,
  USER_ENROLLED_LIVE_CLASS_API,
  USER_PROGRESS_REPORTEES_API,
  ENROLL_LIVE_CLASS_API,
  RESCHEDULE_LIVE_CLASS_API,
  UNENROLL_LIVE_CLASS_API,
  COMPLETED_LIVE_CLASS_API,
  OLE_OFFERING_ROSTER_DOWNLOAD_API,
} from 'config/constants';
import { LiveVirtualClass } from 'types/liveClass';
import type { LmsClassEnrollment } from 'types/class';

interface LvcListResponse {
  items: LiveVirtualClass[];
}

export interface UserLiveClassEnrollment {
  username: string;
  status: string;
  status_code: string;
  offering_id: string;
  offering_slug: string;
  offering_name: string;
  enrollment_time: string;
}

export interface LiveClassReportItem {
  username: string;
  status: string;
  status_code?: string;
  offering_id?: string;
  offering_slug?: string;
  offering_name?: string;
  enrollment_time?: string;
  course_slug?: string;
  course_title?: string;
}

interface LiveClassColumnMeta {
  course_slug?: string;
  offering_slug?: string;
  slug?: string;
  key?: string;
  offering_name?: string;
  course_title?: string;
  title?: string;
  offering_title?: string;
}

interface ManagerLiveClassReportResponse {
  live_class?: {
    rows?: Array<{ username: string; statuses: Record<string, string> }>;
    columns?: LiveClassColumnMeta[];
    by_username?: Record<
      string,
      UserLiveClassEnrollment[] | Record<string, string>
    >;
  };
  items?: LiveClassReportItem[];
  results?: LiveClassReportItem[];
  [username: string]: unknown;
}

interface ClassRosterItem {
  username: string;
  email?: string;
  offering_id?: string;
  offering_slug?: string;
  status?: string;
  [key: string]: unknown;
}

interface ClassRosterResponse {
  items?: ClassRosterItem[];
  results?: ClassRosterItem[];
  data?: ClassRosterItem[];
}

const { get, post } = axios;

export const getManagerLiveClassReport = async ({
  users = [],
  organizationId,
  signal,
}: {
  users?: string[];
  organizationId?: string;
  signal?: AbortSignal;
} = {}) => {
  const extractUserMapItems = (responseData: any = {}) => {
    if (!responseData || Array.isArray(responseData)) return null;
    if (responseData.live_class || responseData.items || responseData.results) {
      return null;
    }

    const items: any[] = [];
    Object.entries(responseData).forEach(([username, userEntries]: any) => {
      if (Array.isArray(userEntries)) {
        userEntries.forEach((entry) => {
          items.push({
            username,
            ...entry,
          });
        });
      } else if (typeof userEntries === 'object' && userEntries) {
        Object.entries(userEntries).forEach(([slug, status]: any) => {
          if (typeof status !== 'string') return;
          items.push({
            username,
            status,
            offering_slug: slug,
          });
        });
      }
    });

    return items;
  };

  const extractLiveClassItems = (responseData: any = {}) => {
    const liveClass = responseData?.live_class;
    if (!liveClass) return null;

    const rows = Array.isArray(liveClass?.rows) ? liveClass.rows : [];
    const byUsername = liveClass?.by_username || {};
    const columns = Array.isArray(liveClass?.columns) ? liveClass.columns : [];

    const columnMetaByKey = columns.reduce((acc, column) => {
      const key =
        column?.course_slug ||
        column?.offering_slug ||
        column?.slug ||
        column?.key;
      if (!key) return acc;
      // eslint-disable-next-line no-param-reassign
      acc[key] = column;
      return acc;
    }, {});

    const items: any[] = [];

    if (rows.length > 0) {
      rows.forEach((row) => {
        const username = row?.username;
        const statuses = row?.statuses || {};
        if (!username || typeof statuses !== 'object') return;

        Object.entries(statuses).forEach(([slug, status]: any) => {
          const meta = columnMetaByKey[slug] || {};
          items.push({
            username,
            status,
            course_slug: slug,
            course_title:
              meta?.offering_name ||
              meta?.course_title ||
              meta?.title ||
              meta?.offering_title,
          });
        });
      });

      return items;
    }

    Object.entries(byUsername).forEach(([username, userEntries]: any) => {
      if (!userEntries) return;

      if (Array.isArray(userEntries)) {
        userEntries.forEach((entry) => {
          items.push({
            username,
            ...entry,
          });
        });
        return;
      }

      if (typeof userEntries === 'object') {
        Object.entries(userEntries).forEach(([key, value]: any) => {
          const meta = columnMetaByKey[key] || {};

          if (typeof value === 'string') {
            items.push({
              username,
              status: value,
              course_slug: key,
              course_title: meta?.course_title || meta?.title,
            });
            return;
          }

          if (typeof value === 'object' && value) {
            items.push({
              username,
              course_slug:
                value.course_slug || value.offering_slug || value.slug || key,
              course_title:
                value.course_title ||
                value.title ||
                meta?.course_title ||
                meta?.title,
              ...value,
            });
          }
        });
      }
    });

    return items;
  };

  const normalizeResponse = (response: {
    data?: ManagerLiveClassReportResponse;
  }): LiveClassReportItem[] => {
    const responseData = response?.data || {};
    const userMapItems = extractUserMapItems(responseData);
    if (userMapItems !== null) {
      return userMapItems;
    }

    const liveClassItems = extractLiveClassItems(responseData);
    if (liveClassItems !== null) {
      return liveClassItems;
    }

    return (responseData?.items ||
      responseData?.results ||
      (responseData?.data as LiveClassReportItem[] | undefined) ||
      []) as LiveClassReportItem[];
  };

  const response = await post<ManagerLiveClassReportResponse>(
    USER_PROGRESS_REPORTEES_API,
    {
      organization: organizationId,
      users,
      report_type: 'live_class',
    },
    { signal },
  );
  return normalizeResponse(response);
};

/**
 * Fetch upcoming live virtual classes (LVC) available for enrollment.
 * These are classes that the student can enroll in.
 * @param offeringCode Optional offering code to filter the classes. E.g., 'rh199'
 * @returns Promise resolving to the API response.
 */
export const getOfferingUpcomingLiveClass = async (offeringSlug?: string) => {
  try {
    const { code: offeringCode } = extractFromSlug(offeringSlug);
    if (offeringCode === '') {
      throw new Error('offeringCode is required');
    }

    const result = await axios.get<LvcListResponse>(UPCOMING_LIVE_CLASS_API, {
      params: { offering_code: offeringCode },
    });

    return result.data;
  } catch (error) {
    throw new Error(error);
  }
};

/**
 * @returns enrolled offering live virtual class
 * This only includes ongoing and upcoming classes the user has already enrolled in. Completed classes are not included.
 * @param offeringCode Optional offering code to filter the classes. E.g., 'rh199'
 * @returns Promise resolving to the API response.
 */
export const getOfferingEnrolledLiveClass = async (offeringSlug: string) => {
  try {
    const { code: offeringCode } = extractFromSlug(offeringSlug);
    if (offeringCode === '') {
      throw new Error('offeringCode is required');
    }

    const result = await axios.get<LvcListResponse>(
      USER_ENROLLED_LIVE_CLASS_API,
      { params: { offering_code: offeringCode } },
    );

    return result.data;
  } catch (error) {
    throw new Error(error);
  }
};

/**
 * Fetch all live virtual classes (LVC) the user is enrolled in, including completed classes.
 * @returns Promise resolving to the API response with all enrolled classes, including completed ones.
 */
export const getAllEnrolledLiveClass = async () => {
  try {
    const response = await axios.get<LvcListResponse>(
      USER_ENROLLED_LIVE_CLASS_API,
      { params: { all: true } },
    );
    return response.data;
  } catch (e) {
    throw e;
  }
};

export const enrollLiveClass = async (offeringId: string) => {
  try {
    if (offeringId === '') {
      throw new Error('offeringId is required');
    }

    const result = await post<LmsClassEnrollment>(
      ENROLL_LIVE_CLASS_API.replace('<<offering_id>>', offeringId),
    );

    return result.data;
  } catch (error) {
    throw error;
  }
};

export const rescheduleLiveClass = async (
  offeringId: string,
  rescheduleFromOfferingId: string,
) => {
  try {
    if (offeringId === '' || rescheduleFromOfferingId === '') {
      throw new Error('offeringId and rescheduleFromOfferingId is required');
    }

    const result = await post<LmsClassEnrollment>(
      RESCHEDULE_LIVE_CLASS_API.replace('<<offering_id>>', offeringId).replace(
        '<<from_offering_id>>',
        rescheduleFromOfferingId,
      ),
    );
    return result.data;
  } catch (error) {
    throw error;
  }
};

export const unenrollLiveClass = async (OfferingID: string) => {
  const url = UNENROLL_LIVE_CLASS_API.replace('<<offering_id>>', OfferingID);
  try {
    const res = await get<{ items: LiveVirtualClass[] }>(url);
    return res.data.items;
  } catch (err) {
    throw err;
  }
};

export const getCompletedLvc = async (offeringCode?: string) => {
  try {
    const response = await axios.get<LvcListResponse>(
      COMPLETED_LIVE_CLASS_API,
      { params: { offering_code: offeringCode } },
    );
    return response.data?.items || [];
  } catch (e) {
    throw e;
  }
};

export const getClassEnrollments = async (
  offeringId: string,
  ignoreInstructors: boolean = false,
) => {
  const response = await axios.get<ClassRosterResponse>(
    OLE_OFFERING_ROSTER_DOWNLOAD_API,
    {
      params: {
        offering_id: offeringId,
        ignore_instructors: ignoreInstructors,
      },
    },
  );
  return response.data;
};

export const downloadClassEnrollmentTable = (
  offeringId: string,
  ignoreInstructors: boolean = false,
) => {
  const params = new URLSearchParams({
    offering_id: offeringId,
    ignore_instructors: String(ignoreInstructors),
    as_csv: 'true',
  });
  window.open(`${OLE_OFFERING_ROSTER_DOWNLOAD_API}?${params}`);
};
